import { useEffect } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { z } from "zod";

import { useConfiguration, useUpdateConfiguration } from "@/api/configuration";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import SettingsCard from "./SettingsCard";

const schema = z.object({
  reschedulingHorizonDays: z
    .number({ error: "Must be a number" })
    .int()
    .min(0, "Must be between 0 and 365 days")
    .max(365, "Must be between 0 and 365 days"),
  acceptableEarlyDays: z
    .number({ error: "Must be a number" })
    .int()
    .min(0, "Must be between 0 and 365 days")
    .max(365, "Must be between 0 and 365 days"),
});

type FormValues = z.infer<typeof schema>;

export default function PlanningSettings() {
  const { t } = useTranslation();
  const configQuery = useConfiguration();
  const update = useUpdateConfiguration();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      reschedulingHorizonDays: 14,
      acceptableEarlyDays: 2,
    },
  });

  useEffect(() => {
    if (configQuery.data) {
      form.reset({
        reschedulingHorizonDays: configQuery.data.reschedulingHorizonDays,
        acceptableEarlyDays: configQuery.data.acceptableEarlyDays,
      });
    }
  }, [configQuery.data, form]);

  const onSubmit = (values: FormValues) => {
    update.mutate(values, {
      onSuccess: () =>
        toast.success(t("settings.saved", { defaultValue: "Saved" })),
      onError: () =>
        toast.error(t("settings.saveFailed", { defaultValue: "Save failed" })),
    });
  };

  return (
    <SettingsCard
      title={t("settings.rail.planning")}
      description={t("settings.configuration.planning.description")}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="reschedulingHorizonDays"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t(
                    "settings.configuration.planning.reschedulingHorizonLabel",
                  )}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    max={365}
                    {...field}
                    onChange={(e) => field.onChange(e.target.valueAsNumber)}
                  />
                </FormControl>
                <FormDescription>
                  {t(
                    "settings.configuration.planning.reschedulingHorizonDescription",
                  )}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="acceptableEarlyDays"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("settings.configuration.planning.acceptableEarlyLabel")}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    max={365}
                    {...field}
                    onChange={(e) => field.onChange(e.target.valueAsNumber)}
                  />
                </FormControl>
                <FormDescription>
                  {t(
                    "settings.configuration.planning.acceptableEarlyDescription",
                  )}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={!form.formState.isDirty || update.isPending}
            >
              {update.isPending
                ? t("settings.configuration.saving")
                : t("common.buttons.save")}
            </Button>
          </div>
        </form>
      </Form>
    </SettingsCard>
  );
}
